import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'organization' | 'website' | 'article' | 'service' | 'localBusiness';
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  section?: string;
}

const BASE_URL = 'https://lqpglobal.com';

const SchemaOrg: React.FC<SchemaOrgProps> = ({
  type,
  title,
  description,
  image,
  url,
  datePublished,
  dateModified,
  section
}) => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LQP Global",
    "alternateName": "LQP",
    "url": BASE_URL,
    "logo": "https://lqpglobal.com/logocubo.png",
    "description": "Soluciones digitales premium para pymes: sistemas a medida, automatización, inteligencia artificial y e-commerce.",
    "foundingDate": "2020",
    "sameAs": [
      "https://www.instagram.com/globallqp",
      "https://www.linkedin.com/company/lqpglobal",
      "https://wa.me/5491139416257"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+54-9-11-3941-6257",
      "contactType": "sales",
      "availableLanguage": ["Spanish"]
    },
    "areaServed": {
      "@type": "Country",
      "name": "Argentina"
    },
    "knowsAbout": [
      "Desarrollo de software a medida",
      "Automatización empresarial",
      "Inteligencia Artificial para negocios",
      "E-commerce",
      "Sistemas de gestión para pymes",
      "Transformación digital"
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "LQP Global",
    "url": BASE_URL,
    "description": "Soluciones digitales premium para pymes",
    "publisher": {
      "@type": "Organization",
      "name": "LQP Global",
      "logo": {
        "@type": "ImageObject",
        "url": "https://lqpglobal.com/logocubo.png"
      }
    }
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "url": url,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": "LQP Global",
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "LQP Global",
      "logo": {
        "@type": "ImageObject",
        "url": "https://lqpglobal.com/logocubo.png",
        "width": 200,
        "height": 200
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "articleSection": section
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "LQP Global - Soluciones Digitales",
    "description": "Desarrollo de software a medida, automatización e inteligencia artificial para pymes",
    "url": BASE_URL,
    "image": "https://lqpglobal.com/logocubo.png",
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Argentina"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de desarrollo",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Desarrollo de software a medida",
            "description": "Sistemas de gestión personalizados para pymes"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Automatización empresarial",
            "description": "Flujos de trabajo automatizados y bots"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Inteligencia Artificial",
            "description": "Integración de IA en procesos de negocio"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "E-commerce",
            "description": "Tiendas online y catálogos B2B"
          }
        }
      ]
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "LQP Global",
    "image": "https://lqpglobal.com/logocubo.png",
    "url": BASE_URL,
    "telephone": "+54-9-11-3941-6257",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AR",
      "addressRegion": "Buenos Aires"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -34.6037,
      "longitude": -58.3816
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  const getSchema = () => {
    switch (type) {
      case 'organization':
        return organizationSchema;
      case 'website':
        return websiteSchema;
      case 'article':
        return articleSchema;
      case 'service':
        return serviceSchema;
      case 'localBusiness':
        return localBusinessSchema;
      default:
        return organizationSchema;
    }
  };

  useEffect(() => {
    const schemaId = `schema-${type}`;
    let scriptElement = document.getElementById(schemaId) as HTMLScriptElement | null;

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = schemaId;
      scriptElement.type = 'application/ld+json';
      document.head.appendChild(scriptElement);
    }

    scriptElement.textContent = JSON.stringify(getSchema());

    return () => {
      // Cleanup on unmount if it's a dynamic schema (like article)
      if (type === 'article') {
        scriptElement?.remove();
      }
    };
  }, [type, title, description, image, url, datePublished, dateModified, section]);

  return null;
};

export default SchemaOrg;
